import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import useLabSounds from "../hooks/useLabSounds";
import { Flame, Target, TrendingUp, ArrowRight, CheckCircle2, Clock, Award, Sparkles, X, Zap, Mail, Terminal, Trophy } from "lucide-react";

// Pretty-name mapping for weak-area codes used in the onboarding form. The
// dashboard welcome banner shows the user's first focus area in the same
// language they picked it. Kept in sync with /app/frontend/src/pages/Onboarding.jsx.
const AREA_NAME = {
  fundamentals: "Network Fundamentals",
  access: "Network Access",
  connectivity: "IP Connectivity",
  services: "IP Services",
  security: "Security",
  automation: "Automation",
};

function ReadinessDial({ value }) {
  const r = 72;
  const circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, value)) / 100) * circ;
  const color = value >= 75 ? "#00FF41" : value >= 40 ? "#00E5FF" : "#F59E0B";
  return (
    <div className="relative w-52 h-52" data-testid="readiness-dial">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={r} stroke="#27272A" strokeWidth="10" fill="none" />
        <circle
          cx="100"
          cy="100"
          r={r}
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 8px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="font-term text-5xl font-bold"
          style={{ color, textShadow: `0 0 12px ${color}55` }}
        >
          {value}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mt-1">
          readiness
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, color }) {
  return (
    <div className="bg-[#141416] border border-zinc-800 p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
        <Icon size={12} style={{ color }} />
        {label}
      </div>
      <div className="font-term text-3xl font-bold mt-2 stat-value" style={{ color }}>
        {value}
      </div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function DomainBar({ d }) {
  const color = d.percentage >= 75 ? "#00FF41" : d.percentage >= 40 ? "#00E5FF" : "#F59E0B";
  // Click scrolls to the corresponding domain section in /labs (no filter).
  return (
    <Link
      to={`/labs#${d.code}`}
      className="block -mx-2 px-2 py-1 group focus:outline-none"
      data-testid={`domain-${d.code}`}
      title={`Jump to ${d.name} on the labs page`}
    >
      <div className="flex justify-between text-xs font-mono mb-1.5">
        <span className="text-zinc-300 group-hover:text-[#00E5FF] transition-colors">{d.name}</span>
        <span className="text-zinc-500">
          {d.completed}/{d.total} · <span style={{ color }}>{d.percentage}%</span>
        </span>
      </div>
      <div className="h-2 bg-zinc-900 border border-zinc-800 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${d.percentage}%`, background: color }} />
      </div>
    </Link>
  );
}

// Round 41 · "Stage 1 CLI Onramp" nudge — celebrates the freshly-expanded
// 15-lab foundations arc. Two visual states:
//   • In progress → segmented terminal-style meter + "continue" CTA jumping
//     to the next unstarted Stage 1 lab.
//   • Complete → green pulsing trophy state pushing users into Stage 3
//     (Switching). Persists as dismissed via localStorage so we don't nag
//     forever, but returning users can always re-open it from /labs.
const ONRAMP_DISMISS_KEY = "hypexlabs.dashboard.onramp.dismissed";
const ONRAMP_CELEBRATED_KEY = "hypexlabs.dashboard.onramp.celebrated";

// Full-viewport one-shot confetti burst. Renders as a `fixed` overlay so
// particles can travel edge-to-edge regardless of where the trigger element
// sits in the layout. 80 particles keep the density right for a widescreen
// canvas without murdering low-end GPUs. Deterministic seed per index so
// React re-renders during the 2.5s burst don't shuffle particles mid-flight.
function CelebrationBurst() {
  const COLORS = ["#00FF41", "#00E5FF", "#F59E0B", "#FF6B35", "#FFFFFF"];
  const particles = Array.from({ length: 80 }, (_, i) => {
    const rand = (seed) => {
      const x = Math.sin(i * 999.13 + seed) * 43758.5453;
      return x - Math.floor(x);
    };
    // Wider spread: ±60vw horizontal, up to -80vh vertical burst height.
    const dx = (rand(1) - 0.5) * 120;              // vw units (see style)
    const dy = -30 - rand(2) * 70;                 // vh units, upward
    const rot = (rand(3) - 0.5) * 1440;            // ±720deg spin
    const delay = rand(4) * 350;                   // 0-350ms staggered
    const color = COLORS[i % COLORS.length];
    const size = 6 + rand(5) * 8;                  // 6-14px
    return { dx, dy, rot, delay, color, size, i };
  });

  return (
    <div
      className="pointer-events-none fixed inset-0 overflow-hidden z-[9999]"
      aria-hidden="true"
      data-testid="onramp-confetti"
    >
      <style>{`
        @keyframes hpx-confetti {
          0%   { transform: translate(0,0) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate(var(--dx), calc(var(--dy) + 90vh)) rotate(var(--rot)); opacity: 0; }
        }
      `}</style>
      {particles.map((p) => (
        <div
          key={p.i}
          className="absolute left-1/2 top-1/2"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            boxShadow: `0 0 8px ${p.color}88`,
            marginLeft: `-${p.size / 2}px`,
            marginTop: `-${p.size / 2}px`,
            "--dx": `${p.dx}vw`,
            "--dy": `${p.dy}vh`,
            "--rot": `${p.rot}deg`,
            animation: `hpx-confetti 2.5s cubic-bezier(0.15, 0.7, 0.35, 1) ${p.delay}ms forwards`,
            borderRadius: p.i % 3 === 0 ? "50%" : "1px",
          }}
        />
      ))}
    </div>
  );
}
function CLIOnrampCard({ stage1, onDismiss }) {
  if (!stage1 || stage1.total === 0) return null;
  const { total, completed, inProgress, nextLabId } = stage1;
  const pct = Math.round((completed / total) * 100);
  const done = completed >= total;

  return (
    <div
      className={`mb-6 relative border p-5 overflow-hidden group transition-all ${
        done
          ? "bg-gradient-to-r from-[#00FF41]/10 via-[#00FF41]/5 to-transparent border-[#00FF41]/50"
          : "bg-gradient-to-r from-[#00E5FF]/8 via-[#00E5FF]/3 to-transparent border-[#00E5FF]/35 hover:border-[#00E5FF]/60"
      }`}
      data-testid="dashboard-onramp-card"
    >
      {done && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle at 20% 30%, rgba(0,255,65,0.12), transparent 60%)",
            animation: "pulse 3s ease-in-out infinite",
          }}
        />
      )}
      <div className="relative flex items-start gap-4">
        <div className={`shrink-0 w-11 h-11 flex items-center justify-center border ${
          done ? "border-[#00FF41]/60 bg-[#00FF41]/10" : "border-[#00E5FF]/40 bg-[#00E5FF]/5"
        }`}>
          {done
            ? <Trophy size={20} className="text-[#00FF41]" style={{ filter: "drop-shadow(0 0 6px #00FF4188)" }} />
            : <Terminal size={20} className="text-[#00E5FF]" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono"
                 style={{ color: done ? "#00FF41" : "#00E5FF" }}>
              // stage 1 · meet the cli
            </div>
            {done ? (
              <div className="text-[10px] uppercase tracking-widest font-mono font-bold text-[#00FF41]">
                COMPLETE ✓
              </div>
            ) : (
              <div className="text-xs text-zinc-500 font-mono">
                <span className="text-zinc-200 font-semibold">{completed}</span>
                <span className="text-zinc-600">/{total}</span>
                {inProgress > 0 && <span className="text-[#00E5FF]"> · {inProgress} in progress</span>}
              </div>
            )}
          </div>

          <h3 className="font-display font-bold text-lg mt-1 tracking-tight">
            {done
              ? "You made it through the fundamentals."
              : "Your CLI foundations arc is unlocked."}
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            {done
              ? "15 hands-on labs — from your first `enable` to a full SSH lockdown. Time to switch things up in Stage 3."
              : "15 hands-on labs walk you from your first ? command to a full SSH lockdown. Ship them in order and the rest of CCNA will feel like reading."}
          </p>

          {/* Terminal-style 15-cell meter */}
          <div className="mt-4 flex items-center gap-3">
            <div className="flex gap-[3px] flex-1 min-w-0" data-testid="onramp-meter">
              {Array.from({ length: total }).map((_, i) => {
                const filled = i < completed;
                return (
                  <div
                    key={i}
                    className={`h-2 flex-1 min-w-0 transition-all ${
                      filled
                        ? done
                          ? "bg-[#00FF41]"
                          : "bg-[#00E5FF]"
                        : "bg-zinc-800 border border-zinc-700/50"
                    }`}
                    style={filled ? { boxShadow: `0 0 6px ${done ? "#00FF41" : "#00E5FF"}88` } : undefined}
                  />
                );
              })}
            </div>
            <div className="text-xs font-term font-bold shrink-0"
                 style={{ color: done ? "#00FF41" : "#00E5FF" }}>
              {pct}%
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Link
              to={done ? "/labs" : (nextLabId ? `/labs/${nextLabId}` : "/labs")}
              onClick={done ? onDismiss : undefined}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest font-bold transition-colors flex items-center gap-2 ${
                done
                  ? "bg-[#00FF41] text-black hover:bg-[#00CC33]"
                  : "bg-[#00E5FF] text-black hover:bg-[#00B8CC]"
              }`}
              data-testid="dashboard-onramp-cta"
            >
              {done ? "explore stage 3 — switching" : "continue onramp"} <ArrowRight size={13} />
            </Link>
            {done && (
              <button
                onClick={onDismiss}
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition-colors"
                data-testid="dashboard-onramp-dismiss-done"
              >
                dismiss
              </button>
            )}
          </div>
        </div>

        {!done && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-zinc-500 hover:text-zinc-100 transition-colors shrink-0 self-start"
            title="Hide until I finish a lab"
            data-testid="dashboard-onramp-dismiss"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, resendVerification, refresh } = useAuth();
  const { playLabRight } = useLabSounds();
  const [progress, setProgress] = useState(null);
  const [recs, setRecs] = useState([]);
  const [billing, setBilling] = useState(null);
  const [stage1, setStage1] = useState(null);   // Round 41 — CLI onramp nudge
  const [celebrating, setCelebrating] = useState(false);   // Round 42 — one-shot burst
  const [onrampDismissed, setOnrampDismissed] = useState(() => {
    try { return localStorage.getItem(ONRAMP_DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [loading, setLoading] = useState(true);
  const [verifyResend, setVerifyResend] = useState({ sending: false, msg: "" });
  // One-time welcome banner state — set during onboarding submit, dismissed
  // either by the user or after the dashboard mounts once.
  const [welcome, setWelcome] = useState(() => {
    try {
      const raw = sessionStorage.getItem("ciscolab.justOnboarded");
      if (!raw) return null;
      sessionStorage.removeItem("ciscolab.justOnboarded");
      return JSON.parse(raw);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: r }, { data: b }, { data: labsData }] = await Promise.all([
          api.get("/progress"),
          api.get("/progress/recommendations"),
          api.get("/billing/status").catch(() => ({ data: null })),
          api.get("/labs").catch(() => ({ data: { stages: [] } })),
        ]);
        setProgress(p);
        setRecs(r.recommendations || []);
        setBilling(b);
        // Derive Stage 1 progress from the /labs stages tree so we don't
        // need a new endpoint. Frontend already has the enriched stage info.
        const meet = (labsData.stages || []).find((s) => s.stage === 1);
        if (meet) {
          const all = meet.sections.flatMap((sec) => sec.labs);
          const completed = all.filter((l) => l.status === "completed").length;
          const inProgress = all.filter((l) => l.status === "in_progress").length;
          // Pick the next lab a user should tackle: prefer in_progress,
          // fall back to first not_started, in section+order sequence.
          const nextInProg = all.find((l) => l.status === "in_progress");
          const nextTodo = all.find((l) => l.status === "not_started");
          setStage1({
            total: all.length,
            completed,
            inProgress,
            nextLabId: (nextInProg || nextTodo)?.id || null,
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dismissOnramp = () => {
    setOnrampDismissed(true);
    try { localStorage.setItem(ONRAMP_DISMISS_KEY, "1"); } catch { /* */ }
  };

  // Round 42 — Fire the lab-completion sound + confetti burst the FIRST
  // time a user's Stage 1 flips to 15/15. Guarded by localStorage so it
  // never repeats, even across sessions or devices (best-effort — browser
  // storage is per-device but that's an acceptable trade-off for a
  // one-shot celebratory moment).
  useEffect(() => {
    if (!stage1) return;
    if (stage1.completed < stage1.total) return;
    let seen = false;
    try { seen = localStorage.getItem(ONRAMP_CELEBRATED_KEY) === "1"; } catch { /* */ }
    if (seen) return;
    setCelebrating(true);
    try { playLabRight(); } catch { /* */ }
    try { localStorage.setItem(ONRAMP_CELEBRATED_KEY, "1"); } catch { /* */ }
    const t = setTimeout(() => setCelebrating(false), 2500);
    return () => clearTimeout(t);
  }, [stage1, playLabRight]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      {/* Round 42.1 — full-viewport celebration burst. Rendered as a sibling
          of <main> so its fixed overlay covers the entire screen instead of
          being clipped to the card. Fires exactly once on 14/15 → 15/15. */}
      {celebrating && <CelebrationBurst />}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        {welcome && (() => {
          const firstArea = (welcome.weak_areas || [])[0];
          const focusName = firstArea ? (AREA_NAME[firstArea] || "Network Fundamentals") : "Network Fundamentals";
          const labCount = (welcome.weak_areas || []).length || 1;
          return (
            <div
              className="mb-8 relative bg-gradient-to-r from-[#00E5FF]/10 via-[#00E5FF]/5 to-transparent border border-[#00E5FF]/40 px-5 py-4 flex items-start gap-3"
              data-testid="dashboard-welcome-banner"
            >
              <Sparkles size={18} className="text-[#00E5FF] shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <span className="font-display font-bold text-base text-zinc-100">Welcome!</span>{" "}
                <span className="text-zinc-300">
                  We've queued up your first{" "}
                  <span className="text-[#00E5FF] font-semibold">{focusName}</span> lab
                  {labCount > 1 ? <> based on your <span className="text-[#00E5FF] font-semibold">{labCount} focus areas</span>.</> : <> based on your focus areas.</>}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setWelcome(null)}
                className="text-zinc-500 hover:text-zinc-100 transition-colors shrink-0"
                title="Dismiss"
                data-testid="dashboard-welcome-dismiss"
              >
                <X size={16} />
              </button>
            </div>
          );
        })()}
        {/* Round 41 — CLI Onramp nudge. Hidden after dismissal via
            localStorage, or while loading. */}
        {!loading && !onrampDismissed && stage1 && (
          <CLIOnrampCard stage1={stage1} onDismiss={dismissOnramp} />
        )}
        {user && user !== false && user.email_verified === false && (
          <div
            className="mb-6 flex items-center justify-between gap-3 px-4 py-3 bg-[#F59E0B]/10 border border-[#F59E0B]/50 text-[#F59E0B]"
            data-testid="dashboard-verify-banner"
          >
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} />
              <span className="font-mono text-xs uppercase tracking-widest">
                Please verify your email to unlock labs. Check{" "}
                <span className="text-zinc-100">{user.email}</span>.
              </span>
            </div>
            <button
              onClick={async () => {
                setVerifyResend({ sending: true, msg: "" });
                try {
                  await resendVerification();
                  setVerifyResend({ sending: false, msg: "Sent — check your inbox." });
                  refresh?.();
                } catch (e) {
                  setVerifyResend({ sending: false, msg: e?.response?.data?.detail || "Could not resend" });
                }
              }}
              disabled={verifyResend.sending}
              className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] font-bold bg-[#F59E0B] text-black hover:bg-[#D97706] transition-colors shrink-0 disabled:opacity-50"
              data-testid="dashboard-resend-verify"
            >
              {verifyResend.sending ? "sending…" : verifyResend.msg ? "resent ✓" : "resend"}
            </button>
          </div>
        )}
        {billing && billing.tier === "free" && (() => {
          const remaining = Math.max(0, (billing.labs_limit ?? 3) - (billing.labs_used ?? 0));
          const exhausted = remaining === 0;
          return (
            <div
              className={`mb-6 flex items-center justify-between gap-3 px-4 py-3 border ${
                exhausted
                  ? "bg-[#F59E0B]/10 border-[#F59E0B]/50 text-[#F59E0B]"
                  : "bg-zinc-900/40 border-zinc-800 text-zinc-300"
              }`}
              data-testid="dashboard-free-tier-banner"
            >
              <div className="flex items-center gap-2 text-sm">
                <Zap size={14} className={exhausted ? "text-[#F59E0B]" : "text-[#00E5FF]"} />
                <span className="font-mono text-xs uppercase tracking-widest">
                  {exhausted ? (
                    <>You've used your 3 free labs this month. <span className="text-[#F59E0B]">Upgrade to Pro for unlimited access.</span></>
                  ) : (
                    <>{remaining} free lab{remaining === 1 ? "" : "s"} remaining this month</>
                  )}
                </span>
              </div>
              <Link
                to="/pricing"
                className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] font-bold transition-colors shrink-0 ${
                  exhausted
                    ? "bg-[#F59E0B] text-black hover:bg-[#D97706]"
                    : "bg-[#00E5FF] text-black hover:bg-[#00B8CC]"
                }`}
                data-testid="dashboard-free-tier-upgrade"
              >
                upgrade →
              </Link>
            </div>
          );
        })()}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-1">
              // welcome, {user?.name?.split(" ")[0] || "engineer"}
            </div>
            <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter">
              Your CCNA command center.
            </h1>
          </div>
          <Link
            to="/labs"
            className="px-5 py-2.5 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2"
            data-testid="dashboard-browse-labs"
          >
            browse all labs <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm" data-testid="dashboard-loading">loading…</div>
        ) : (
          <div className="grid lg:grid-cols-[340px_1fr] gap-8">
            <div className="bg-[#141416] border border-zinc-800 p-6 flex flex-col items-center" data-testid="readiness-card">
              <ReadinessDial value={progress?.readiness_score || 0} />
              <div className="mt-6 text-center">
                <div className="text-xs text-zinc-400">Weighted across all 6 CCNA domains.</div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <Stat
                  icon={Flame}
                  label="streak"
                  value={`${progress?.streak_days || 0}d`}
                  hint="consecutive practice days"
                  color="#F59E0B"
                />
                <Stat
                  icon={Award}
                  label="completed"
                  value={`${progress?.completed_labs || 0}/${progress?.total_labs || 0}`}
                  hint={`${progress?.in_progress_labs || 0} in progress`}
                  color="#00FF41"
                />
                <Stat
                  icon={Clock}
                  label="this week"
                  value={progress?.labs_this_week || 0}
                  hint="labs attempted"
                  color="#00E5FF"
                />
              </div>

              {/* Domain progress */}
              <div className="bg-[#141416] border border-zinc-800 p-6" data-testid="domain-progress">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono flex items-center gap-2">
                    <TrendingUp size={12} /> domain progress
                  </div>
                  <Link to="/progress" className="text-xs text-[#00E5FF] hover:underline font-mono">
                    view detail →
                  </Link>
                </div>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                  {progress?.domains?.map((d) => (
                    <DomainBar key={d.code} d={d} />
                  ))}
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-[#141416] border border-zinc-800 p-6" data-testid="recommendations">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono flex items-center gap-2">
                    <Target size={12} /> recommended next
                  </div>
                  {progress?.weak_areas?.[0] && (
                    <div className="text-xs text-zinc-500 font-mono">
                      focus: <span className="text-[#F59E0B]">{progress.weak_areas[0].name}</span>
                    </div>
                  )}
                </div>
                {recs.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-4 flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-[#00FF41]" />
                    You've completed everything available. Great job!
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {recs.map((l) => (
                      <Link
                        key={l.id}
                        to={`/labs/${l.id}`}
                        className="block p-4 bg-black border border-zinc-800 hover:border-[#00E5FF] transition-all group"
                        data-testid={`rec-lab-${l.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                          {l.domain}
                        </div>
                        <div className="font-display font-bold mt-1 group-hover:text-[#00E5FF] transition-colors">
                          {l.title}
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-2 text-zinc-500">
                          <span className="font-mono">{l.topic}</span>
                          <span>·</span>
                          <span className="font-mono uppercase">{l.difficulty}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
